import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import { useParams, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { 
  FiArrowLeft, 
  FiPackage, 
  FiLayers, 
  FiBox, 
  FiAlertCircle, 
  FiDownload, 
  FiCheckCircle, 
  FiXCircle 
} from "react-icons/fi";
import * as XLSX from "xlsx";

function VendorView() {
  const { uuid } = useParams();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true); // Start as true to prevent flash of empty state

  const fetchVendorProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getVendorProductsFullDetailByUUID/${uuid}`);
      // Safety check for response data structure
      if (res.data) {
        setVendor(res.data.vendor || null);
        setProducts(res.data.products || []);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      Swal.fire("Error", "Failed to fetch vendor products", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (uuid) {
      fetchVendorProducts();
    }
  }, [uuid]);

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await axios.put(`${baseurl}updateVendorStatus/${id}`, { status: newStatus });
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: `Vendor is now ${newStatus}`,
        showConfirmButton: false,
        timer: 2000,
      });
      fetchVendorProducts();
    } catch (err) {
      Swal.fire("Error", "Update failed", "error");
    }
  };

  const exportToExcel = () => {
    if (!products || products.length === 0) return Swal.fire("Info", "No data to export", "info");
    const data = products.map((p) => ({
      Name: p.name || "N/A",
      SKU: p.sku || "N/A",
      Category: `${p.category_name || ""} > ${p.subcategory_name || ""}`,
      Price: `₹${p.price || 0}`,
      Stock: p.stock_quantity || 0,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventory");
    XLSX.writeFile(wb, `${vendor?.business_name || "Vendor"}_Inventory.xlsx`);
  };

  // Safe Summary calculations with fallbacks
  const stats = [
    { label: "Total Catalog", value: products?.length || 0, icon: <FiPackage />, color: "#4f46e5", bg: "#eef2ff" },
    { label: "With Variants", value: products?.filter(p => p.variants?.length > 0).length || 0, icon: <FiLayers />, color: "#0891b2", bg: "#ecfeff" },
    { label: "Simple Items", value: products?.filter(p => !p.variants || p.variants.length === 0).length || 0, icon: <FiBox />, color: "#059669", bg: "#ecfdf5" },
    { label: "Low Stock", value: products?.filter(p => (p.stock_quantity || 0) < 10).length || 0, icon: <FiAlertCircle />, color: "#dc2626", bg: "#fef2f2" },
  ];

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper" style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
        <div className="container-fluid p-4">
          
          <div className="mb-4">
            <Link to="/vendor-status" className="text-decoration-none text-muted small d-flex align-items-center gap-2 mb-2">
              <FiArrowLeft /> Back to Vendor Status
            </Link>
          </div>

          {loading ? (
            <div className="d-flex flex-column justify-content-center align-items-center py-5">
               <div className="spinner-border text-primary mb-2"></div>
               <span className="text-muted">Loading Vendor Data...</span>
            </div>
          ) : vendor ? (
            <>
              {/* Profile Header Card */}
              <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
                <div className="card-body p-0">
                  <div className="p-4 d-flex flex-wrap justify-content-between align-items-center bg-white">
                    <div className="d-flex align-items-center gap-3">
                      <div className="vendor-avatar-lg">
                        {vendor.name ? vendor.name.charAt(0) : "V"}
                      </div>
                      <div>
                        <h3 className="fw-bold mb-0">{vendor.business_name || "Unknown Business"}</h3>
                        <p className="text-muted mb-0">{vendor.name} • {vendor.email}</p>
                        <span className="badge bg-light text-dark border mt-1">UUID: {vendor.uuid}</span>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button 
                        className={`btn px-4 rounded-pill d-flex align-items-center gap-2 ${vendor.status === 'active' ? 'btn-success' : 'btn-warning'}`}
                        onClick={() => toggleStatus(vendor.id, vendor.status)}
                      >
                        {vendor.status === 'active' ? <FiCheckCircle /> : <FiXCircle />}
                        {/* THE FIX: Added Optional Chaining */}
                        {(vendor.status || "").toUpperCase()}
                      </button>
                      <button className="btn btn-outline-primary px-4 rounded-pill d-flex align-items-center gap-2" onClick={exportToExcel}>
                        <FiDownload /> Export CSV
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* KPI Section */}
              <div className="row g-3 mb-4">
                {stats.map((s, i) => (
                  <div className="col-md-3" key={i}>
                    <div className="card border-0 shadow-sm rounded-4 h-100 p-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className="kpi-icon-box" style={{ backgroundColor: s.bg, color: s.color }}>
                          {s.icon}
                        </div>
                        <div>
                          <p className="text-muted small fw-bold mb-0 text-uppercase">{s.label}</p>
                          <h4 className="fw-bold mb-0">{s.value}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Inventory Table */}
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="card-header bg-white py-3 border-0">
                  <h5 className="fw-bold mb-0">Product Inventory</h5>
                </div>
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Product Info</th>
                        <th>Category</th>
                        <th>Price</th>
                        <th>Stock</th>
                        <th className="text-end pe-4">Variants</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.length > 0 ? products.map((p) => (
                        <React.Fragment key={p.id}>
                          <tr>
                            <td className="ps-4">
                              <div className="fw-bold text-dark">{p.name}</div>
                              <div className="text-muted smaller">SKU: {p.sku}</div>
                            </td>
                            <td>
                              <span className="badge bg-blue-subtle text-primary">{p.category_name}</span>
                              <div className="smaller text-muted">{p.subcategory_name}</div>
                            </td>
                            <td className="fw-bold text-dark">₹{p.price}</td>
                            <td>
                              <div className={`fw-bold ${p.stock_quantity < 10 ? 'text-danger' : 'text-success'}`}>
                                {p.stock_quantity} <span className="smaller fw-normal text-muted">pcs</span>
                              </div>
                            </td>
                            <td className="text-end pe-4">
                              {p.variants?.length > 0 ? (
                                <button 
                                  className="btn btn-sm btn-light border rounded-pill px-3"
                                  data-bs-toggle="collapse" 
                                  data-bs-target={`#var-${p.id}`}
                                >
                                  Details ({p.variants.length})
                                </button>
                              ) : <span className="text-muted small">None</span>}
                            </td>
                          </tr>
                          
                          {p.variants?.length > 0 && (
                            <tr className="collapse bg-light" id={`var-${p.id}`}>
                              <td colSpan="5" className="p-4">
                                <div className="row g-3">
                                  {p.variants.map((v, idx) => (
                                    <div className="col-lg-3 col-md-4" key={idx}>
                                      <div className="card border-0 shadow-sm rounded-3 h-100 variant-card">
                                        <img 
                                          src={v.variant_json?.images?.[0]?.path ? `${imgurl}${v.variant_json.images[0].path}` : "https://via.placeholder.com/150"} 
                                          className="card-img-top p-2 rounded-4"
                                          alt="variant"
                                          style={{ height: "120px", objectFit: "contain" }}
                                        />
                                        <div className="card-body p-3 pt-0">
                                          <div className="fw-bold small">{v.variant_json?.name || "Unnamed Variant"}</div>
                                          <div className="d-flex justify-content-between mt-2">
                                            <span className="smaller text-muted">₹{v.variant_json?.price}</span>
                                            <span className="smaller fw-bold text-primary">{v.variant_json?.stock} In Stock</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )) : (
                        <tr><td colSpan="5" className="text-center py-4 text-muted">No products listed for this vendor.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-5">
                <h4>Vendor Not Found</h4>
                <p className="text-muted">The requested vendor UUID does not exist or has been removed.</p>
                <Link to="/vendors" className="btn btn-primary rounded-pill">Return to Directory</Link>
            </div>
          )}
        </div>
      </main>

      <style>{`
        .bg-blue-subtle { background-color: #e0f2fe; }
        .smaller { font-size: 0.75rem; }
        
        .vendor-avatar-lg {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #4f46e5, #06b6d4);
          color: white;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: bold;
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
        }

        .kpi-icon-box {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }

        .variant-card {
          transition: transform 0.2s;
          border: 1px solid transparent !important;
        }
        .variant-card:hover {
          transform: translateY(-3px);
          border-color: #4f46e5 !important;
        }

        .table thead th {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          color: #64748b;
          letter-spacing: 0.025em;
          border: none;
        }
      `}</style>
    </>
  );
}

export default VendorView;